import { useMemo } from "react";
import ReactEcharts from "echarts-for-react";

const CORPORATE = {
    text: "#64748b",
    textDark: "#334155",
    value: "#0f172a",
    grid: "#edf2f7",
    axis: "#cbd5e1",
    primary: "#76b82a",
    blue: "#2563eb",
};

function buildGaugeOption(title, value, unit, max, color) {
    const numeric = Number(value);
    const safeValue = Number.isFinite(numeric) ? numeric : 0;

    return {
        series: [
            {
                type: "gauge",
                min: 0,
                max,
                splitNumber: 4,
                radius: "88%",
                center: ["50%", "56%"],
                axisLine: {
                    lineStyle: {
                        width: 8,
                        color: [[0.7, "#86efac"], [0.9, "#fde047"], [1, "#fca5a5"]],
                    },
                },
                pointer: { width: 3, length: "55%" },
                axisTick: { distance: -12, length: 4 },
                splitLine: { distance: -14, length: 8 },
                axisLabel: { distance: 12, fontSize: 9, color: CORPORATE.text },
                detail: {
                    valueAnimation: true,
                    formatter: `{value}${unit}`,
                    fontSize: 15,
                    fontWeight: 700,
                    color: CORPORATE.value,
                    offsetCenter: [0, "24%"],
                },
                title: {
                    offsetCenter: [0, "72%"],
                    fontSize: 11,
                    color: CORPORATE.text,
                },
                data: [{ value: safeValue, name: title, itemStyle: { color } }],
            },
        ],
    };
}

function buildPhaseBarOption(rectifier) {
    return {
        tooltip: { trigger: "axis" },
        legend: {
            data: ["Voltage (V)", "Current (A)"],
            textStyle: { color: CORPORATE.text, fontSize: 10 },
            top: 0,
            itemWidth: 10,
            itemHeight: 10,
        },
        grid: { left: 32, right: 12, top: 32, bottom: 20 },
        xAxis: {
            type: "category",
            data: ["R", "Y", "B"],
            axisLabel: { color: CORPORATE.textDark, fontSize: 11 },
            axisLine: { lineStyle: { color: CORPORATE.axis } },
        },
        yAxis: {
            type: "value",
            axisLabel: { color: CORPORATE.text, fontSize: 10 },
            splitLine: { lineStyle: { color: CORPORATE.grid } },
        },
        series: [
            {
                name: "Voltage (V)",
                type: "bar",
                barWidth: 16,
                itemStyle: { color: CORPORATE.blue, borderRadius: [4, 4, 0, 0] },
                data: [
                    Number(rectifier?.pahse_avoltage) || 0,
                    Number(rectifier?.pahse_bvoltage) || 0,
                    Number(rectifier?.pahse_cvoltage) || 0,
                ],
            },
            {
                name: "Current (A)",
                type: "bar",
                barWidth: 16,
                itemStyle: { color: CORPORATE.primary, borderRadius: [4, 4, 0, 0] },
                data: [
                    Number(rectifier?.pahse_acurrent) || 0,
                    Number(rectifier?.pahse_bcurrent) || 0,
                    Number(rectifier?.pahse_ccurrent) || 0,
                ],
            },
        ],
    };
}

function buildTrendOption(history) {
    return {
        tooltip: { trigger: "axis" },
        legend: {
            data: ["DC Voltage", "DC Current"],
            textStyle: { color: CORPORATE.text, fontSize: 10 },
            top: 0,
            itemWidth: 10,
            itemHeight: 10,
        },
        grid: { left: 36, right: 12, top: 32, bottom: 22 },
        xAxis: {
            type: "category",
            boundaryGap: false,
            data: history.labels,
            axisLabel: { color: CORPORATE.text, fontSize: 9 },
            axisLine: { lineStyle: { color: CORPORATE.axis } },
        },
        yAxis: [
            {
                type: "value",
                name: "V",
                nameTextStyle: { color: CORPORATE.text, fontSize: 10 },
                axisLabel: { color: CORPORATE.text, fontSize: 10 },
                splitLine: { lineStyle: { color: CORPORATE.grid } },
            },
            {
                type: "value",
                name: "A",
                nameTextStyle: { color: CORPORATE.text, fontSize: 10 },
                axisLabel: { color: CORPORATE.text, fontSize: 10 },
                splitLine: { show: false },
            },
        ],
        series: [
            {
                name: "DC Voltage",
                type: "line",
                smooth: true,
                showSymbol: false,
                lineStyle: { width: 2, color: CORPORATE.blue },
                areaStyle: { color: "rgba(37, 99, 235, 0.08)" },
                data: history.dc_voltage,
            },
            {
                name: "DC Current",
                type: "line",
                smooth: true,
                yAxisIndex: 1,
                showSymbol: false,
                lineStyle: { width: 2, color: CORPORATE.primary },
                areaStyle: { color: "rgba(118, 184, 42, 0.08)" },
                data: history.dc_current,
            },
        ],
    };
}

export default function RectifierLiveCharts({ rectifier, history, updateTime }) {
    const dcVoltageOption = useMemo(
        () => buildGaugeOption("DC Voltage", rectifier?.dc_voltage, "V", 60, CORPORATE.blue),
        [rectifier?.dc_voltage]
    );
    const dcCurrentOption = useMemo(
        () => buildGaugeOption("DC Current", rectifier?.dc_current, "A", 300, CORPORATE.primary),
        [rectifier?.dc_current]
    );
    const phaseBarOption = useMemo(() => buildPhaseBarOption(rectifier), [rectifier]);
    const trendOption = useMemo(() => buildTrendOption(history), [history]);

    return (
        <div className="rectifier-charts">
            <div className="rectifier-charts__toolbar">
                <span className="rectifier-charts__title">Realtime Charts</span>
                <span className="rectifier-charts__divider">|</span>
                <span className="rectifier-charts__meta">{updateTime || "—"}</span>
                <span className="rectifier-charts__divider">|</span>
                <span className="rectifier-charts__live">
                    <span className="rectifier-live-dot is-live" />
                    Live
                </span>
            </div>

            <div className="rectifier-charts__grid">
                <div className="rectifier-chart-card">
                    <ReactEcharts option={dcVoltageOption} style={{ height: 148 }} notMerge lazyUpdate />
                </div>
                <div className="rectifier-chart-card">
                    <ReactEcharts option={dcCurrentOption} style={{ height: 148 }} notMerge lazyUpdate />
                </div>
                <div className="rectifier-chart-card rectifier-chart-card--wide">
                    <div className="rectifier-chart-card__title">3-Phase Input</div>
                    <ReactEcharts option={phaseBarOption} style={{ height: 168 }} notMerge lazyUpdate />
                </div>
                <div className="rectifier-chart-card rectifier-chart-card--wide">
                    <div className="rectifier-chart-card__title">DC Telemetry Trend</div>
                    <ReactEcharts option={trendOption} style={{ height: 168 }} notMerge lazyUpdate />
                </div>
            </div>
        </div>
    );
}
